import { useEffect, useState } from "react";
import type { UploadItem } from "../hooks/useUpload";

interface Props {
  item: UploadItem;
  extension: string;
  showNameField: boolean;
  onNameChange: (name: string) => void;
  onRetry: () => void;
  onRemove: () => void;
  onCancel: () => void;
}

const STATUS_META: Record<UploadItem["status"], { label: string; color: string }> = {
  queued: { label: "Queued", color: "text-zinc-400" },
  uploading: { label: "Uploading", color: "text-zinc-600" },
  done: { label: "Done", color: "text-emerald-600" },
  error: { label: "Failed", color: "text-red-600" },
  cancelled: { label: "Cancelled", color: "text-zinc-400" },
};

export function UploadQueueItem({ item, extension, showNameField, onNameChange, onRetry, onRemove, onCancel }: Props) {
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(item.file);
    setThumb(url);
    return () => URL.revokeObjectURL(url);
  }, [item.file]);

  const meta = STATUS_META[item.status];
  const barColor =
    item.status === "error" ? "bg-red-400" : item.status === "done" ? "bg-emerald-500" : "bg-zinc-800";
  const inProgress = item.status === "uploading" || item.status === "queued";

  return (
    <li className="glass glass-quiet flex items-center gap-3 rounded-xl p-2.5">
      <div className="checkerboard h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg border border-white/70">
        {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" />}
      </div>

      <div className="min-w-0 flex-1">
        {showNameField ? (
          <div className="flex items-stretch">
            <input
              value={item.name}
              onChange={(e) => onNameChange(e.target.value)}
              disabled={item.status === "uploading" || item.status === "done"}
              className="field-sm w-full min-w-0 rounded-r-none disabled:text-zinc-400"
            />
            <span className="grid h-9 flex-shrink-0 place-items-center rounded-r-lg border border-l-0 border-zinc-900/10 bg-white/50 px-2 text-[13px] text-zinc-500">
              .{extension}
            </span>
          </div>
        ) : (
          <p className="truncate text-[13px] font-medium text-zinc-800">{item.file.name}</p>
        )}

        <div className="mt-2 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-zinc-900/10">
            <div
              className={`h-full rounded-full transition-all duration-200 ${barColor}`}
              style={{ width: `${item.status === "done" ? 100 : item.progress}%` }}
            />
          </div>
          <span className={`w-16 flex-shrink-0 text-right text-2xs font-medium ${meta.color}`}>{meta.label}</span>
        </div>
        {item.error && <p className="mt-1 text-2xs text-red-600">{item.error}</p>}
      </div>

      <div className="flex flex-shrink-0 items-center gap-1">
        {item.status === "error" && (
          <button
            onClick={onRetry}
            className="rounded-lg px-2 py-1 text-2xs font-semibold text-zinc-900 transition-colors hover:bg-white/80"
          >
            Retry
          </button>
        )}
        <button
          onClick={inProgress ? onCancel : onRemove}
          className="grid h-7 w-7 place-items-center rounded-lg text-zinc-400 transition-colors hover:bg-white/80 hover:text-zinc-800"
          title={inProgress ? "Cancel" : "Remove"}
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>
    </li>
  );
}
